import { queryOptions, useQuery } from '@tanstack/react-query';
import { publicApi } from '@/lib/api-clients/rest-client';
import {
  MarketSpec,
  OrderBook,
  useMarketStore,
} from '@/store/use-markets-store';
import {
  parseDecimalToBigInt,
  parseScientificNotationToDecimalStr,
} from '@/utils/value-format';
import { QueryConfig } from '@/lib/api-clients/react-query';
import { AssetType, MarketStatus } from '@/types';
import { DEFAULT_TICK_SIZES } from '@/config/default-tick-sizes';

type SymbolInfo = {
  symbol: string;
  displayName: string;
  displaySymbol: string;
  base: string;
  quote: string;
  sizeDecimals: number;
  priceDecimals: number;
  initMarginRatio: string;
  maintMarginRatio: string;
  takerFee: string;
  isolated: boolean;
  asset: AssetType;
  tradingStatus: MarketStatus;
  tickSizes: string[];
};

type ExchangeInfo = {
  symbols: SymbolInfo[];
  exchange: {
    lp: string;
    insurance: string;
  };
};

export const FEE_DECIMALS = 5n;

const getExchangeInfo = async (symbols?: string): Promise<ExchangeInfo> => {
  return publicApi
    .get<ExchangeInfo>('/exchangeInfo', {
      params: {
        symbols,
      },
    })
    .then((res: ExchangeInfo) => {
      const { symbols } = res;
      symbols.map((symbolInfo: SymbolInfo) => {
        const store = useMarketStore(symbolInfo.symbol);
        const { setMarketSpec } = store.getState();
        const marketSpec: MarketSpec = {
          displayName: symbolInfo.displayName,
          displaySymbol: symbolInfo.displaySymbol,
          base: symbolInfo.base,
          quote: symbolInfo.quote,
          sizeDecimals: BigInt(symbolInfo.sizeDecimals),
          priceDecimals: BigInt(symbolInfo.priceDecimals),
          initMarginRatio: parseDecimalToBigInt(symbolInfo.initMarginRatio, 4n),
          maintMarginRatio: parseDecimalToBigInt(
            symbolInfo.maintMarginRatio,
            4n,
          ),
          takerFee: parseDecimalToBigInt(symbolInfo.takerFee, FEE_DECIMALS),
          isolated: symbolInfo.isolated,
          status: symbolInfo.tradingStatus ?? MarketStatus.TRADING,
          asset: symbolInfo.asset,
          tickSizes: symbolInfo.tickSizes,
          defaultTickSize:
            symbolInfo.symbol in DEFAULT_TICK_SIZES
              ? parseScientificNotationToDecimalStr(
                  DEFAULT_TICK_SIZES[symbolInfo.symbol],
                )
              : symbolInfo.tickSizes[0] ?? '',
        };
        store.setState({
          books: symbolInfo.tickSizes.reduce(
            (prev, size) => ({ ...prev, [size]: { asks: [], bids: [] } }),
            {} as Record<string, OrderBook>,
          ),
        });
        setMarketSpec(marketSpec);
      });
      return res;
    })
    .catch((error) => {
      console.error(error);
      throw error;
    });
};

const getExchangeInfoQueryOptions = ({
  symbols,
}: {
  symbols?: string;
} = {}) =>
  queryOptions({
    queryKey: symbols ? ['exchangeInfo', { symbols }] : ['exchangeInfo'],
    queryFn: () => getExchangeInfo(symbols),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

type UseExchangeInfoOptions = {
  symbols?: string;
  queryConfig?: QueryConfig<typeof getExchangeInfo>;
};

export const useExchangeInfo = ({
  queryConfig,
  symbols,
}: UseExchangeInfoOptions = {}) => {
  return useQuery({
    ...getExchangeInfoQueryOptions({ symbols }),
    ...queryConfig,
  });
};
